import React, { useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { tokenManager } from 'utils/tokenManager';

const AgentRegistration = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    contact: "",
    location: "",
    nationalId: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [phoneError, setPhoneError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for contact (phone number)
    if (name === "contact") {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 9 digits
      const formattedValue = digitsOnly.slice(0, 9);
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      
      // Validate phone number in real-time
      if (formattedValue.length === 9) {
        const validPrefixes = ['20', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
        const phonePrefix = formattedValue.substring(0, 2);
        
        if (!validPrefixes.includes(phonePrefix)) {
          setPhoneError("Invalid phone number format. Please enter a valid Ghanaian mobile number.");
        } else {
          setPhoneError("");
        }
      } else if (formattedValue.length > 0) {
        setPhoneError("Phone number must be exactly 9 digits");
      } else {
        setPhoneError("");
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number format
    if (formData.contact.length !== 9) {
      showToast("Contact number must be exactly 9 digits", "error");
      return;
    }
    
    // Validate Ghanaian mobile number patterns
    const validPrefixes = ['20', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
    const phonePrefix = formData.contact.substring(0, 2);
    
    if (!validPrefixes.includes(phonePrefix)) {
      showToast("Invalid phone number format. Please enter a valid Ghanaian mobile number", "error");
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Prepare the API payload
      const apiPayload = {
        full_name: formData.fullName,
        contact: formData.contact,
        location: formData.location,
        national_id: formData.nationalId,
      };

      // Make API call (replace with your actual endpoint)
      const response = await fetch('https://usibras-web-backend.onrender.com/api/agents', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });

      const result = await response.json();
      
      console.log("API Response:", result);
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }
      
      showToast(result.message || `Agent registered successfully! Agent ID: ${result.agent_id || 'N/A'}`, "success");
      
      // Reset form
      setFormData({
        fullName: "",
        contact: "",
        location: "",
        nationalId: "",
      });
      setPhoneError("");
      
    } catch (error) {
      console.error("API Error:", error);
      showToast(error.message || "Failed to submit registration. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/newlogo-removebg-preview.png" 
              alt="USIBRAS Ghana Limited Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <h2 className="mb-2 text-4xl font-bold text-navy-700 dark:text-white">
            USIBRAS GHANA LTD
          </h2>
          <h3 className="mb-4 text-2xl font-semibold text-navy-600 dark:text-gray-300">
            AGENT REGISTRATION
          </h3>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Complete the form below to register as an agent
          </p>
        </div>

        {/* Registration Form */}
        <Card extra="shadow-xl">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Agent Information */}
              <div>
                <h4 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                  Agent Information
                </h4>
                
                <div className="space-y-4">
                  <InputField
                    label="Full Name"
                    id="fullName"
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />

                  <div>
                    <label htmlFor="contact" className="text-sm text-navy-700 dark:text-white ml-3 font-bold mb-2 block">
                      Contact Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="contact"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      required
                      maxLength={9}
                      pattern="[0-9]{9}"
                      placeholder="e.g., 245678901"
                      className={`mt-2 block w-full rounded-md border px-3 py-2 text-sm shadow-sm outline-none placeholder:text-gray-400 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 ${
                        phoneError 
                          ? 'border-red-500 text-red-700 dark:border-red-500 dark:text-red-400' 
                          : 'border-gray-300 text-gray-700 dark:border-gray-600'
                      }`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter 9 digits only (e.g., 245678901). Do not include (0), +233, or 233.
                    </p>
                    {phoneError && (
                      <p className="mt-1 text-xs text-red-500">
                        {phoneError}
                      </p>
                    )}
                  </div>

                  <InputField
                    label="Location"
                    id="location"
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter location/address"
                  />

                  <InputField
                    label="National ID Number"
                    id="nationalId"
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter National ID or Voter's ID"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center h-12 rounded-xl bg-brand-500 text-white font-medium hover:bg-brand-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                >
                  {isSubmitting ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        <span>Processing Registration...</span>
                      </div>
                      {/* Animated background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                    </>
                  ) : (
                    "Submit Registration"
                  )}
                </button>
                
                {/* Modern Loading Overlay */}
                {isSubmitting && (
                  <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-sm w-full mx-4">
                      <div className="text-center">
                        {/* Loading Text */}
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                          Submitting Registration
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                          Please wait while we process your information...
                        </p>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                          <div className="bg-gradient-to-r from-brand-500 to-brand-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                        </div>
                        
                        {/* Loading Dots */}
                        <div className="flex justify-center space-x-1">
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                  Please ensure all required fields are completed before submitting.
                </p>
              </div>
            </form>
          </div>
        </Card>

        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm transition-all duration-300 ${
            toast.type === "success" 
              ? "bg-green-500 text-white" 
              : toast.type === "error"
              ? "bg-red-500 text-white"
              : toast.type === "warning"
              ? "bg-yellow-500 text-white"
              : "bg-blue-500 text-white"
          }`}>
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0">
                {toast.type === "success" && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === "error" && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === "warning" && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === "info" && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{toast.message}</p>
              </div>
              <button
                onClick={() => setToast({ show: false, message: "", type: "success" })}
                className="flex-shrink-0 ml-2 text-white hover:text-gray-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AgentRegistration;

